import { useRef, useState } from "react";
import api, { errorMessage, resolveMediaUrl } from "../lib/api";
import { GhostButton } from "./Primitives";
import MediaImage from "./MediaImage";
import { Camera, Upload, X, Loader2, Clock, Video, Film } from "lucide-react";
import { toast } from "sonner";
import { watermarkPhoto, getHeading, formatSiteAddress } from "../lib/watermark";

/**
 * Multi-photo (+ optional video) upload field with client-side watermarking
 * for field-report contexts (incident / DAR / maintenance).
 *
 * When `site` is passed, the watermark burns the site's full street address
 * into every image before upload. GPS + timestamp are always stamped from
 * the browser sensors when available. Video uploads bypass the watermark
 * (a static overlay wouldn't make sense) and are size + duration capped
 * (iter214: 25 MB, 30s, 480p target — enforced client-side).
 *
 * Props:
 *   value: PhotoAttachment[] — mixed photo + video attachments
 *   onChange: (next) => void
 *   purpose: "incident" | "dar" | "maintenance" | "other"
 *   site?: Site record (for the address watermark)
 *   guardName?: string — burned into the watermark alongside GPS
 *   allowVideo?: boolean (default false; enable for incident + maintenance)
 *   testIdPrefix?: string
 */
const VIDEO_MAX_BYTES = 25 * 1024 * 1024;   // 25 MB
const VIDEO_MAX_SECS = 30;

const isVideo = (att) =>
  (att?.content_type || "").startsWith("video/") ||
  /\.(mp4|mov|webm|m4v)$/i.test(att?.url || "");

export function PhotoAttachmentsField({
  value = [],
  onChange,
  purpose = "incident",
  site,
  guardName,
  allowVideo = false,
  testIdPrefix = "photo",
}) {
  const fileRef = useRef(null);
  const videoRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const captureLoc = () =>
    new Promise((resolve) => {
      if (!navigator.geolocation) return resolve({});
      navigator.geolocation.getCurrentPosition(
        (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
        () => resolve({}),
        { timeout: 5000 }
      );
    });

  const _uploadFile = async (file, loc, isMedia = false) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("purpose", purpose);
    fd.append("captured_at", new Date(file.lastModified || Date.now()).toISOString());
    if (loc.lat !== undefined) fd.append("lat", loc.lat);
    if (loc.lng !== undefined) fd.append("lng", loc.lng);
    const endpoint = isMedia ? "/uploads/media" : "/uploads/photo";
    const res = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
    return res.data;
  };

  const onPickPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const loc = await captureLoc();
      const heading = await getHeading();
      const address = formatSiteAddress(site);
      const uploaded = [];
      for (const raw of files) {
        // Watermark burns timestamp + address + GPS + heading + guard name
        // into a bottom band. Silently falls back to raw file on decode
        // failure so a corrupted image doesn't block the whole batch.
        let stamped = raw;
        try {
          stamped = await watermarkPhoto(raw, {
            timestamp: new Date(raw.lastModified || Date.now()).toISOString(),
            address,
            lat: loc.lat,
            lng: loc.lng,
            headingDeg: heading,
            guardName,
          });
        } catch (err) {
          if (process.env.NODE_ENV === "development") console.warn("Watermark skipped:", err);
        }
        uploaded.push(await _uploadFile(stamped, loc, false));
      }
      onChange([...(value || []), ...uploaded]);
      toast.success(`${uploaded.length} photo${uploaded.length === 1 ? "" : "s"} attached`);
    } catch (err) {
      toast.error(errorMessage(err, "Photo upload failed"));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onPickVideo = async (e) => {
    const file = (e.target.files || [])[0];
    if (!file) return;

    if (file.size > VIDEO_MAX_BYTES) {
      toast.error(`Video is too large — 25 MB max`);
      if (videoRef.current) videoRef.current.value = "";
      return;
    }
    // Probe duration locally so a 5-minute clip doesn't hit the server.
    const durationOk = await new Promise((resolve) => {
      const v = document.createElement("video");
      v.preload = "metadata";
      v.onloadedmetadata = () => {
        URL.revokeObjectURL(v.src);
        resolve(v.duration <= VIDEO_MAX_SECS + 0.5); // small slop
      };
      v.onerror = () => resolve(true); // let the server reject if we can't probe
      v.src = URL.createObjectURL(file);
    });
    if (!durationOk) {
      toast.error(`Video is too long — ${VIDEO_MAX_SECS}s max`);
      if (videoRef.current) videoRef.current.value = "";
      return;
    }
    // Probe resolution too — a 25MB / 30s clip at 4K is theoretically
    // possible and would defeat the storage-cost intent behind the 480p
    // target. We can't transcode in-browser so we just warn + reject.
    const dimsOk = await new Promise((resolve) => {
      const v = document.createElement("video");
      v.preload = "metadata";
      v.onloadedmetadata = () => {
        URL.revokeObjectURL(v.src);
        const shorterSide = Math.min(v.videoWidth || 0, v.videoHeight || 0);
        // Allow up to 720p in practice — reject 1080p+ so the "480p target"
        // stays a soft ceiling (broad enough for mobile-recorded clips).
        resolve(shorterSide === 0 || shorterSide <= 720);
      };
      v.onerror = () => resolve(true);
      v.src = URL.createObjectURL(file);
    });
    if (!dimsOk) {
      toast.error("Video resolution is too high — 720p max (480p recommended)");
      if (videoRef.current) videoRef.current.value = "";
      return;
    }

    setUploading(true);
    try {
      const loc = await captureLoc();
      const rec = await _uploadFile(file, loc, true);
      onChange([...(value || []), rec]);
      toast.success("Video attached");
    } catch (err) {
      toast.error(errorMessage(err, "Video upload failed"));
    } finally {
      setUploading(false);
      if (videoRef.current) videoRef.current.value = "";
    }
  };

  const removeAt = (idx) => onChange(value.filter((_, i) => i !== idx));
  const updateCaption = (idx, caption) =>
    onChange(value.map((p, i) => (i === idx ? { ...p, caption } : p)));

  return (
    <div data-testid={`${testIdPrefix}-field`}>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        onChange={onPickPhotos}
        className="hidden"
        data-testid={`${testIdPrefix}-input`}
      />
      {allowVideo && (
        <input
          ref={videoRef}
          type="file"
          accept="video/*"
          capture="environment"
          onChange={onPickVideo}
          className="hidden"
          data-testid={`${testIdPrefix}-video-input`}
        />
      )}
      <div className="flex gap-2 items-center mb-3 flex-wrap">
        <GhostButton type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid={`${testIdPrefix}-add`}>
          {uploading ? <Loader2 className="size-3.5 animate-spin" /> : <Upload className="size-3.5" />} Attach photo
        </GhostButton>
        <GhostButton
          type="button"
          onClick={() => { fileRef.current?.setAttribute("capture", "environment"); fileRef.current?.click(); }}
          disabled={uploading}
        >
          <Camera className="size-3.5" /> Take photo
        </GhostButton>
        {allowVideo && (
          <>
            <GhostButton
              type="button"
              onClick={() => { videoRef.current?.removeAttribute("capture"); videoRef.current?.click(); }}
              disabled={uploading}
              data-testid={`${testIdPrefix}-video-add`}
              title="Attach video from your gallery (25 MB / 30s max)"
            >
              <Film className="size-3.5" /> Attach video
            </GhostButton>
            <GhostButton
              type="button"
              onClick={() => { videoRef.current?.setAttribute("capture", "environment"); videoRef.current?.click(); }}
              disabled={uploading}
              data-testid={`${testIdPrefix}-video-record`}
              title="Record a new video with your camera (25 MB / 30s max)"
            >
              <Video className="size-3.5" /> Record video
            </GhostButton>
          </>
        )}
        <span className="text-xs font-mono text-zinc-500">{value.length} attached</span>
      </div>
      {value.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid={`${testIdPrefix}-grid`}>
          {value.map((p, i) => {
            const src = resolveMediaUrl(p.url);
            const video = isVideo(p);
            return (
              <div key={p.url || i} className="relative border border-[#27272A] bg-[#0A0A0A] group" data-testid={`${testIdPrefix}-item-${i}`}>
                {video ? (
                  <video src={src} controls className="w-full h-32 object-cover bg-black" preload="metadata" />
                ) : (
                  <MediaImage
                    src={src}
                    alt={p.caption || `Photo ${i + 1}`}
                    className="w-full h-32 object-cover"
                    testId={`${testIdPrefix}-item-img-${i}`}
                  />
                )}
                <button
                  type="button"
                  onClick={() => removeAt(i)}
                  className="absolute top-1 right-1 bg-black/70 border border-[#27272A] p-1 hover:border-[#FF3B30] hover:text-[#FF3B30]"
                  data-testid={`${testIdPrefix}-remove-${i}`}
                  title="Remove"
                >
                  <X className="size-3" />
                </button>
                {p.captured_at && (
                  <div className="absolute bottom-8 left-0 right-0 bg-black/70 text-[10px] font-mono text-zinc-300 px-2 py-1 flex items-center gap-1">
                    <Clock className="size-3" />
                    {new Date(p.captured_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                    {video && <span className="ml-auto uppercase text-[#FF9F0A]">VIDEO</span>}
                  </div>
                )}
                <input
                  type="text"
                  placeholder="Caption (optional)"
                  value={p.caption || ""}
                  onChange={(e) => updateCaption(i, e.target.value)}
                  className="w-full text-xs bg-[#121212] border-t border-[#27272A] px-2 py-1 outline-none focus:border-[#FF3B30]"
                  data-testid={`${testIdPrefix}-caption-${i}`}
                />
              </div>
            );
          })}
        </div>
      )}
      {allowVideo && (
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-2">
          Videos: 25 MB · 30 s max · 480p target
        </div>
      )}
    </div>
  );
}

export default PhotoAttachmentsField;
